import { initializeApp } from 'firebase/app'

const env = (key: string) => (import.meta.env[key] as string || '').trim()

const firebaseConfig = {
  apiKey: env('VITE_FIREBASE_API_KEY'),
  authDomain: env('VITE_FIREBASE_AUTH_DOMAIN'),
  databaseURL: env('VITE_FIREBASE_DATABASE_URL'),
  projectId: env('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: env('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: env('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: env('VITE_FIREBASE_APP_ID'),
}

const app = initializeApp(firebaseConfig)

// Lazy-loaded Firebase modules — only loaded when needed
let _auth: import('firebase/auth').Auth | null = null

// Firestore: Promise 기반 싱글턴으로 동시 호출 시 race condition 방지
let _firestorePromise: Promise<import('firebase/firestore').Firestore> | null = null

export async function getAuthInstance() {
  if (!_auth) {
    const { getAuth } = await import('firebase/auth')
    _auth = getAuth(app)
  }
  return _auth
}

// Firestore 설정 버전 — 설정이 변경되면 이 값을 올려서 이전 캐시 자동 정리
const FIRESTORE_CONFIG_VERSION = 5 // v5: 손상된 IndexedDB 캐시 정리 (INTERNAL ASSERTION FAILED 해소)

export async function getFirestoreInstance() {
  if (!_firestorePromise) {
    _firestorePromise = (async () => {
      // 이전 버전의 Firestore IndexedDB 캐시 정리
      await clearStaleFirestoreCache()
      cleanFirestoreLocalStorage()

      const mod = await import('firebase/firestore')
      let firestore: import('firebase/firestore').Firestore
      try {
        // persistentLocalCache + forceLongPolling 조합
        //   - IndexedDB: resume token 보존 → 재연결 시 누락 없이 이어받기
        //   - Long Polling: 매 요청이 독립 HTTP → WebSocket 무소음 사망 불가
        // 실패 이력 (memoryLocalCache):
        //   + autoDetectLongPolling → INTERNAL ASSERTION FAILED
        //   + forceLongPolling → 시간 후 연결 사망 (resume token 미보존)
        //   + 기본 WebSocket → 시간 후 끊김
        // 실패 이력 (persistentLocalCache):
        //   + 기본 WebSocket → 에러 없이 무소음 사망
        firestore = mod.initializeFirestore(app, {
          localCache: mod.persistentLocalCache({
            tabManager: mod.persistentSingleTabManager({}),
          }),
          experimentalForceLongPolling: true,
        })
      } catch {
        firestore = mod.getFirestore(app)
      }
      setupConnectionRecovery(firestore, mod.enableNetwork)
      return firestore
    })()
  }
  return _firestorePromise
}

// 이전 버전의 Firestore IndexedDB/localStorage 캐시 정리
async function clearStaleFirestoreCache() {
  const CACHE_VERSION_KEY = 'tdl_firestore_config_v'
  try {
    const stored = localStorage.getItem(CACHE_VERSION_KEY)
    const currentVersion = String(FIRESTORE_CONFIG_VERSION)
    if (stored === currentVersion) return

    // 버전 변경 감지 — Firestore IndexedDB 전체 삭제
    if (stored !== null) {
      console.info(`[Firebase] Firestore 설정 변경 (v${stored}→v${currentVersion}), 캐시 정리`)
      try {
        const dbs = await indexedDB.databases?.() || []
        for (const db of dbs) {
          // Firestore 캐시만 삭제 — Auth/FCM/Installations DB 보존!
          if (db.name && db.name.includes('firestore') && !db.name.includes('firebaseLocalStorage')) {
            indexedDB.deleteDatabase(db.name)
          }
        }
      } catch {
        try { indexedDB.deleteDatabase('firestore/[DEFAULT]/moonwave-todolist-v1/main') } catch { /* */ }
      }
    }

    localStorage.setItem(CACHE_VERSION_KEY, currentVersion)
  } catch { /* */ }
}

// Firestore가 localStorage에 쌓는 메타데이터(targets, mutations 등)가
// 브라우저 quota(5-10MB)를 초과하면 INTERNAL ASSERTION FAILED 에러 발생.
// 앱 시작 시 1MB 이상인 stale 항목을 정리.
function cleanFirestoreLocalStorage() {
  try {
    const keysToDelete: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('firestore_') || key.startsWith('fs_'))) {
        const value = localStorage.getItem(key)
        if (value && value.length > 1024 * 1024) { // 1MB 이상이면 삭제
          keysToDelete.push(key)
        }
      }
    }
    if (keysToDelete.length > 0) {
      keysToDelete.forEach((key) => localStorage.removeItem(key))
      console.log(`[Firebase] stale localStorage 항목 ${keysToDelete.length}개 정리`)
    }
  } catch {
    // localStorage 접근 실패 무시 (시크릿 모드 등)
  }
}

// 연결 복구 콜백 레지스트리 — 구독 레이어에서 리스너 생존 확인용
type RecoveryCallback = () => void
const _recoveryListeners = new Set<RecoveryCallback>()

export function onConnectionRecovery(callback: RecoveryCallback): () => void {
  _recoveryListeners.add(callback)
  return () => { _recoveryListeners.delete(callback) }
}

function notifyRecoveryListeners() {
  _recoveryListeners.forEach(cb => { try { cb() } catch { /* */ } })
}

// Firestore WebSocket 연결 복구 — 백그라운드 탭 복귀 및 네트워크 재연결 시
let _recoverySetup = false
function setupConnectionRecovery(
  firestore: import('firebase/firestore').Firestore,
  enableNetwork: (fs: import('firebase/firestore').Firestore) => Promise<void>,
) {
  if (_recoverySetup) return
  _recoverySetup = true

  // 탭이 다시 보이면 Firestore 연결 재활성화 + 구독 복구 알림
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      enableNetwork(firestore).then(() => notifyRecoveryListeners()).catch(() => {})
    }
  })

  // 네트워크 복구 시 Firestore 연결 재활성화 + 구독 복구 알림
  window.addEventListener('online', () => {
    enableNetwork(firestore).then(() => notifyRecoveryListeners()).catch(() => {})
  })
}

// Lazy-loaded firebase/firestore utilities
export async function getFirestoreUtils() {
  const [firestoreMod, firestoreInstance] = await Promise.all([
    import('firebase/firestore'),
    getFirestoreInstance(),
  ])
  return {
    firestore: firestoreInstance,
    collection: firestoreMod.collection,
    doc: firestoreMod.doc,
    setDoc: firestoreMod.setDoc,
    updateDoc: firestoreMod.updateDoc,
    deleteDoc: firestoreMod.deleteDoc,
    getDocs: firestoreMod.getDocs,
    onSnapshot: firestoreMod.onSnapshot,
  }
}

// Lazy-loaded Realtime Database
let _rtdbPromise: Promise<import('firebase/database').Database> | null = null

export async function getRtdbInstance() {
  if (!_rtdbPromise) {
    _rtdbPromise = (async () => {
      const { getDatabase } = await import('firebase/database')
      return getDatabase(app)
    })()
  }
  return _rtdbPromise
}

// Lazy-loaded Firebase Functions
let _functions: import('firebase/functions').Functions | null = null

export async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions')
    _functions = getFunctions(app, 'asia-northeast3')
  }
  return _functions
}

// Lazy-loaded Firebase Messaging
let _messaging: import('firebase/messaging').Messaging | null = null

export async function getMessagingInstance() {
  if (!_messaging) {
    const { getMessaging } = await import('firebase/messaging')
    _messaging = getMessaging(app)
  }
  return _messaging
}

export async function isMessagingSupported(): Promise<boolean> {
  try {
    const { isSupported } = await import('firebase/messaging')
    return await isSupported()
  } catch {
    return false
  }
}

export { app }
